import React, { useState, useEffect } from "react";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import { Table, Input, Space, Tag, Card } from "antd";
import { SearchOutlined, PlusOutlined, AppstoreOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import type { IProduct } from "../../interfaces/product";

export const ProductList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<IProduct[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList<IProduct>({
        resource: "Product",
    });

    useEffect(() => {
        if (!data?.data) return;

        let products = data.data as IProduct[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            products = products.filter((product: IProduct) =>
                product.itemName?.toLowerCase().includes(searchLower) ||
                product.itemCode?.toLowerCase().includes(searchLower) ||
                product.barcode?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...products].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Item Code",
            dataIndex: "itemCode",
            key: "itemCode",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value || "-"}</span>
            ),
            sorter: (a: IProduct, b: IProduct) => (a.itemCode || "").localeCompare(b.itemCode || ""),
        },
        {
            title: "Product Name",
            dataIndex: "itemName",
            key: "itemName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            sorter: (a: IProduct, b: IProduct) => (a.itemName || "").localeCompare(b.itemName || ""),
        },
        {
            title: "Type",
            dataIndex: "itemTypeCode",
            key: "itemTypeCode",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Barcode",
            dataIndex: "barcode",
            key: "barcode",
            render: (value: string) => (
                <span className="text-text-muted text-sm">{value || "-"}</span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            sorter: (a: IProduct, b: IProduct) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: IProduct) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Products"
                icon={<AppstoreOutlined />}
                breadcrumbs={[{ title: "Products" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/products/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by product name, code, or barcode"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};

export default ProductList;
